'use client';

import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { Suspense, useState } from 'react';
import { useIntl } from 'react-intl';
import logTrailDetailsDifficultyTapped from '@alltrails/amplitude/events/logTrailDetailsDifficultyTapped';
import IconButton from '@alltrails/denali/components/IconButton';
import Link from '@alltrails/denali/components/Link';
import EditIcon from '@alltrails/denali/icons/Edit';
import DifficultyIcon from '@alltrails/difficulty/components/DifficultyIcon';
import { getTrailDifficultyFromNumber, getTrailDifficultyLabel } from '@alltrails/difficulty/utils/trailDifficultyHelpers';
import { CommunityRating } from '@alltrails/types/CommunityRating';
import useTrailEditToolbar from '@/hooks/useTrailEditToolbar';
import styles from './Difficulty.module.scss';

const DifficultyModal = dynamic(() => import('@alltrails/difficulty/components/DifficultyModal'), { ssr: false });

type DifficultyProps = {
  difficulty: number;
  isAdminOrPLPUser?: boolean;
  communityRating?: CommunityRating;
  trailId: number;
};

const Difficulty = ({ difficulty, isAdminOrPLPUser, communityRating, trailId }: DifficultyProps) => {
  const [isDifficultyModalOpen, setIsDifficultyModalOpen] = useState(false);
  const { openTrailEditToolbar } = useTrailEditToolbar();
  const intl = useIntl();
  const trailDifficulty = getTrailDifficultyFromNumber(difficulty);
  const difficultyText = getTrailDifficultyLabel(intl, trailDifficulty);

  return (
    <>
      <span data-testid="trail-difficulty" className={classNames({ [styles.adminDifficulty]: isAdminOrPLPUser })}>
        <div className={styles.difficultyContainerWithIcon}>
          <div className={styles.difficultyIcon}>
            <DifficultyIcon difficulty={difficulty} />
          </div>
          <Link
            className={styles.difficultyLink}
            onClick={() => {
              logTrailDetailsDifficultyTapped({
                trail_difficulty: trailDifficulty,
                trail_id: trailId
              });
              window.Sprig?.('identifyAndTrack', {
                eventName: 'trail_details_difficulty_tapped'
              });
              setIsDifficultyModalOpen(true);
            }}
            variant="primary"
            testId="difficulty-level-button"
            title={difficultyText}
          >
            {difficultyText}
          </Link>
        </div>
        {isAdminOrPLPUser && (
          <IconButton
            icon={{ Component: EditIcon }}
            className={styles.editIcon}
            testId="edit-icon"
            variant="primary"
            onClick={() => openTrailEditToolbar()}
            size="sm"
            title={intl.formatMessage({ defaultMessage: 'Edit' })}
          />
        )}
      </span>
      {isDifficultyModalOpen && (
        <Suspense fallback={null}>
          <DifficultyModal
            isOpen={isDifficultyModalOpen}
            onClose={() => setIsDifficultyModalOpen(false)}
            difficulty={difficulty}
            communityRating={communityRating}
            trailId={trailId}
          />
        </Suspense>
      )}
    </>
  );
};

export default Difficulty;
